/**
 * IMAGE-CROP-UX-A1 — pure center-cover crop geometry for fixed-avatar surfaces.
 * No React, DOM, or File I/O. Callers own orchestration.
 */

export const MIN_ZOOM = 1;
export const MAX_ZOOM = 3;
export const INITIAL_ZOOM = 1;
export const NUDGE_PX = 4;
export const NUDGE_SHIFT_PX = 12;

export function isValidSourceSize(sourceWidth, sourceHeight) {
  return (
    Number.isFinite(sourceWidth) &&
    Number.isFinite(sourceHeight) &&
    sourceWidth > 0 &&
    sourceHeight > 0
  );
}

export function isValidViewportSize(viewportSize) {
  return Number.isFinite(viewportSize) && viewportSize > 0;
}

/**
 * Center-cover base scale so the image fully covers the square viewport at zoom=1.
 */
export function computeBaseScale(sourceWidth, sourceHeight, viewportSize) {
  if (!isValidSourceSize(sourceWidth, sourceHeight) || !isValidViewportSize(viewportSize)) {
    return null;
  }
  return Math.max(viewportSize / sourceWidth, viewportSize / sourceHeight);
}

export function clampZoom(zoom) {
  if (!Number.isFinite(zoom)) return INITIAL_ZOOM;
  if (zoom < MIN_ZOOM) return MIN_ZOOM;
  if (zoom > MAX_ZOOM) return MAX_ZOOM;
  return zoom;
}

export function computeEffectiveScale(sourceWidth, sourceHeight, viewportSize, zoom) {
  const base = computeBaseScale(sourceWidth, sourceHeight, viewportSize);
  if (base == null) return null;
  return base * clampZoom(zoom);
}

export function computeMaxOffsets(sourceWidth, sourceHeight, viewportSize, zoom) {
  const effectiveScale = computeEffectiveScale(
    sourceWidth,
    sourceHeight,
    viewportSize,
    zoom,
  );
  if (effectiveScale == null) {
    return { maxOffsetX: 0, maxOffsetY: 0, effectiveScale: null };
  }
  const renderedWidth = sourceWidth * effectiveScale;
  const renderedHeight = sourceHeight * effectiveScale;
  return {
    maxOffsetX: Math.max(0, (renderedWidth - viewportSize) / 2),
    maxOffsetY: Math.max(0, (renderedHeight - viewportSize) / 2),
    effectiveScale,
  };
}

/**
 * Clamp translation so no empty region enters the crop viewport.
 * Does not mutate the caller object.
 */
export function clampOffset(offsetX, offsetY, sourceWidth, sourceHeight, viewportSize, zoom) {
  const { maxOffsetX, maxOffsetY } = computeMaxOffsets(
    sourceWidth,
    sourceHeight,
    viewportSize,
    zoom,
  );
  const x = Number.isFinite(offsetX) ? offsetX : 0;
  const y = Number.isFinite(offsetY) ? offsetY : 0;
  return {
    offsetX: Math.min(maxOffsetX, Math.max(-maxOffsetX, x)),
    offsetY: Math.min(maxOffsetY, Math.max(-maxOffsetY, y)),
  };
}

/**
 * Source-space crop rectangle derived from the same visible transform.
 * Returns integers clamped inside source bounds, or null on invalid input.
 */
export function getSourceCropRect(sourceWidth, sourceHeight, viewportSize, zoom, offsetX, offsetY) {
  const effectiveScale = computeEffectiveScale(
    sourceWidth,
    sourceHeight,
    viewportSize,
    zoom,
  );
  if (effectiveScale == null) return null;

  const clamped = clampOffset(
    offsetX,
    offsetY,
    sourceWidth,
    sourceHeight,
    viewportSize,
    zoom,
  );

  const sourceCropSide = viewportSize / effectiveScale;
  const sourceCenterX = sourceWidth / 2 - clamped.offsetX / effectiveScale;
  const sourceCenterY = sourceHeight / 2 - clamped.offsetY / effectiveScale;

  let left = sourceCenterX - sourceCropSide / 2;
  let top = sourceCenterY - sourceCropSide / 2;

  // Clamp rectangle inside source; keep side within remaining bounds.
  const maxSide = Math.min(sourceWidth, sourceHeight);
  const side = Math.min(sourceCropSide, maxSide);

  if (left < 0) left = 0;
  if (top < 0) top = 0;
  if (left + side > sourceWidth) left = sourceWidth - side;
  if (top + side > sourceHeight) top = sourceHeight - side;

  // Guard floating-point drift at edges.
  left = Math.max(0, Math.min(left, sourceWidth - side));
  top = Math.max(0, Math.min(top, sourceHeight - side));

  if (!(side > 0) || left < 0 || top < 0) return null;

  return {
    x: left,
    y: top,
    width: side,
    height: side,
  };
}

export function createInitialCropState(sourceWidth, sourceHeight, viewportSize) {
  if (!isValidSourceSize(sourceWidth, sourceHeight) || !isValidViewportSize(viewportSize)) {
    return null;
  }
  return {
    zoom: INITIAL_ZOOM,
    offsetX: 0,
    offsetY: 0,
    rotation: 0,
    naturalWidth: sourceWidth,
    naturalHeight: sourceHeight,
    sourceWidth,
    sourceHeight,
    viewportSize,
  };
}

/**
 * Rotate ±90° and swap logical source dimensions so crop geometry stays valid.
 * Resets offsets/zoom to a fresh cover frame for the new orientation.
 */
export function withRotation(state, deltaDegrees) {
  if (!state) return null;
  const delta = Number(deltaDegrees);
  if (!Number.isFinite(delta) || delta === 0) return state;
  const nextRotation = (((state.rotation || 0) + delta) % 360 + 360) % 360;
  const normalized =
    nextRotation === 90 || nextRotation === 180 || nextRotation === 270
      ? nextRotation
      : 0;
  const swap = Math.abs(delta) % 180 === 90;
  const sourceWidth = swap ? state.sourceHeight : state.sourceWidth;
  const sourceHeight = swap ? state.sourceWidth : state.sourceHeight;
  const naturalWidth = state.naturalWidth || state.sourceWidth;
  const naturalHeight = state.naturalHeight || state.sourceHeight;
  const next = createInitialCropState(
    sourceWidth,
    sourceHeight,
    state.viewportSize,
  );
  if (!next) return null;
  return {
    ...next,
    rotation: normalized,
    naturalWidth,
    naturalHeight,
    sourceWidth,
    sourceHeight,
  };
}

/**
 * Apply a new zoom and re-clamp offsets so the crop stays valid.
 */
export function withZoom(state, nextZoom) {
  if (!state) return null;
  const zoom = clampZoom(nextZoom);
  const clamped = clampOffset(
    state.offsetX,
    state.offsetY,
    state.sourceWidth,
    state.sourceHeight,
    state.viewportSize,
    zoom,
  );
  return {
    ...state,
    zoom,
    offsetX: clamped.offsetX,
    offsetY: clamped.offsetY,
  };
}

/**
 * Nudge image by rendered pixels (positive dx moves image right).
 */
export function nudgeCrop(state, dx, dy) {
  if (!state) return null;
  return {
    ...state,
    ...clampOffset(
      state.offsetX + dx,
      state.offsetY + dy,
      state.sourceWidth,
      state.sourceHeight,
      state.viewportSize,
      state.zoom,
    ),
  };
}

export function panCrop(state, dx, dy) {
  return nudgeCrop(state, dx, dy);
}

export function nudgeAmountForKeyboard(shiftKey) {
  return shiftKey ? NUDGE_SHIFT_PX : NUDGE_PX;
}
